import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  ShieldCheck,
  Percent,
  CheckCircle2,
  XCircle,
  Zap,
  CreditCard,
  ArrowRight,
  Sparkles,
  Lock,
} from "lucide-react";

// Subtitle timestamps synchronized to 46s @ 30fps = 1380 frames
const SUBTITLES = [
  {
    from: 0,
    to: 165,
    text: "Pernah kepikiran gak, kenapa harga top-up Free Fire di game jauh lebih mahal?",
  },
  {
    from: 165,
    to: 300,
    text: "Banyak yang ngira selisihnya jauh karena jalurnya ilegal atau rawan minus.",
  },
  {
    from: 300,
    to: 465,
    text: "Padahal bukan itu alasannya! Rahasianya cuma di komisi platform Google Play 30%.",
  },
  {
    from: 465,
    to: 690,
    text: "Lewat in-app purchase, paket 720 Diamond harganya bisa tembus Rp 140.000!",
  },
  {
    from: 690,
    to: 930,
    text: "Tapi di KyuStore direct payment, 100% legal harganya cuma Rp 93.500.",
  },
  {
    from: 930,
    to: 1110,
    text: "Kalian langsung hemat Rp 46.500 tanpa kena potongan komisi raksasa itu.",
  },
  {
    from: 1110,
    to: 1380,
    text: "Pintar-pintarnya milih jalur beli! Cek perbandingan harganya di link bio KyuStore.",
  },
];

export const KyuStoreFFBreakdown: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Active subtitle in golden safe band
  const activeSub = SUBTITLES.find(
    (s) => frame >= s.from && frame < s.to
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0A0D14",
        fontFamily: "'Inter', sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* 1. MASTER AUDIO TRACKS */}
      <Audio
        src={staticFile("audio/ff_guide_vo_mastered.mp3")}
        volume={1.0}
      />
      <Audio
        src={staticFile("bgm-clean.mp3")}
        volume={(f) =>
          interpolate(f, [0, 20, 1340, 1380], [0, 0.12, 0.12, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          })
        }
      />

      {/* SFX Triggers */}
      {frame >= 0 && frame < 30 && (
        <Audio src={staticFile("whoosh.mp3")} volume={0.4} />
      )}
      {frame >= 180 && frame < 210 && (
        <Audio src={staticFile("sfx_card_flip.mp3")} volume={0.5} />
      )}
      {frame >= 480 && frame < 510 && (
        <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />
      )}
      {frame >= 750 && frame < 780 && (
        <Audio src={staticFile("sfx_cash_register.mp3")} volume={0.5} />
      )}
      {frame >= 960 && frame < 990 && (
        <Audio src={staticFile("sfx_level_up.mp3")} volume={0.5} />
      )}

      {/* 2. SOLID MATTE AMBIENT GLOW (ZERO CYBER GRID PATTERNS) */}
      <div
        style={{
          position: "absolute",
          top: -150,
          left: 100,
          width: 880,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(14, 165, 233, 0.16) 0%, rgba(10, 13, 20, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 300,
          right: -100,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, rgba(10, 13, 20, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* 3. MAIN PAIRED STAGE (100% HORIZONTALLY CENTERED - SAFE ZONE Y: 240 - 1460px) */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 80,
          right: 80,
          height: 1180,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          zIndex: 20,
        }}
      >
        {frame < 180 && <HookScene frame={frame} fps={fps} />}
        {frame >= 180 && frame < 480 && (
          <MythScene frame={frame - 180} fps={fps} />
        )}
        {frame >= 480 && frame < 960 && (
          <ComparisonScene frame={frame - 480} fps={fps} />
        )}
        {frame >= 960 && <CTAScene frame={frame - 960} fps={fps} />}
      </div>

      {/* 4. GOLDEN SUBTITLE CONTAINER (SAFE ZONE Y: 1470 - 1520px / BOTTOM: 400px) */}
      {activeSub && (
        <div
          style={{
            position: "absolute",
            bottom: 400,
            left: 80,
            right: 80,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              padding: "16px 36px",
              backgroundColor: "rgba(10, 14, 22, 0.96)",
              border: "2px solid rgba(255, 255, 255, 0.22)",
              borderRadius: 24,
              boxShadow: "0 20px 50px rgba(0, 0, 0, 0.9)",
              maxWidth: 920,
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 34,
                fontWeight: 900,
                lineHeight: 1.35,
                color: "#FFFFFF",
                letterSpacing: -0.5,
              }}
            >
              {activeSub.text}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};

// ==========================================
// SCENE 1: THE HOOK (0 - 180 frames)
// ==========================================
const HookScene: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const charFloat = Math.sin(frame / 20) * 8;
  const diamondRot = Math.sin(frame / 25) * 10;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        transform: `scale(${pop})`,
      }}
    >
      {/* Eyebrow Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 24px",
          borderRadius: 30,
          backgroundColor: "#0284C7",
          marginBottom: 20,
        }}
      >
        <Zap size={22} color="#FDE047" />
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 26,
            letterSpacing: 2,
            color: "#FFFFFF",
          }}
        >
          FREE FIRE • TOP-UP BREAKDOWN
        </span>
      </div>

      {/* Massive Punchy Headline */}
      <h1
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 94,
          lineHeight: 0.94,
          letterSpacing: 2.5,
          margin: "0 0 28px 0",
          textTransform: "uppercase",
          color: "#FFFFFF",
        }}
      >
        KENAPA DI WEB STORE
        <br />
        <span style={{ color: "#38BDF8" }}>BISA JAUH LEBIH MURAH?</span>
      </h1>

      {/* Hero Presentation Split Card */}
      <div
        style={{
          width: 920,
          height: 600,
          position: "relative",
          borderRadius: 28,
          backgroundColor: "rgba(18, 22, 32, 0.96)",
          border: "2px solid rgba(56, 189, 248, 0.4)",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.8)",
          padding: "32px 36px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {/* Left Side: Data comparison */}
        <div
          style={{
            width: 530,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            zIndex: 2,
          }}
        >
          {/* Case study tag */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 16px",
              borderRadius: 16,
              backgroundColor: "rgba(245, 158, 11, 0.18)",
              border: "1px solid rgba(245, 158, 11, 0.5)",
              color: "#FBBF24",
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 22,
              letterSpacing: 1.5,
              marginBottom: 20,
              width: "fit-content",
            }}
          >
            <Sparkles size={18} color="#FBBF24" />
            STUDI KASUS: 720 DIAMOND FREE FIRE
          </div>

          {/* In-Game Tag */}
          <div
            style={{
              padding: "16px 22px",
              borderRadius: 16,
              backgroundColor: "rgba(38, 20, 24, 0.85)",
              border: "1.5px solid rgba(239, 68, 68, 0.45)",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontSize: 16,
                color: "#F87171",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              In-Game Direct Purchase
            </div>
            <div
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 54,
                lineHeight: 1.05,
                color: "#FCA5A5",
                letterSpacing: 1,
              }}
            >
              RP 140.000
            </div>
          </div>

          {/* KyuStore Tag */}
          <div
            style={{
              padding: "16px 22px",
              borderRadius: 16,
              backgroundColor: "rgba(16, 38, 35, 0.92)",
              border: "2px solid #34D399",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  color: "#34D399",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                KyuStore Web Distributor
              </div>
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 8,
                  backgroundColor: "#FBBF24",
                  color: "#0A0D14",
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 18,
                  letterSpacing: 1,
                  fontWeight: 900,
                }}
              >
                HEMAT RP 46.500
              </span>
            </div>
            <div
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 60,
                lineHeight: 1.05,
                color: "#38BDF8",
                letterSpacing: 1,
              }}
            >
              RP 93.500
            </div>
          </div>

          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#94A3B8",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <CheckCircle2 size={22} color="#34D399" />
            <span>100% Legal, Aman & Bergaransi Anti-Minus</span>
          </div>
        </div>

        {/* Right Side: Kelly Character + 3D Diamond */}
        <div
          style={{
            width: 320,
            height: "100%",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            zIndex: 1,
          }}
        >
          <Img
            src={staticFile("ff/freefire_diamond_3d.png")}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 120,
              height: 120,
              objectFit: "contain",
              transform: `rotate(${diamondRot}deg)`,
              filter: "drop-shadow(0 10px 25px rgba(56, 189, 248, 0.4))",
            }}
          />

          <Img
            src={staticFile("ff/freefire_kelly_official.png")}
            style={{
              height: 540,
              objectFit: "contain",
              transform: `translateY(${charFloat}px)`,
              filter: "drop-shadow(0 16px 30px rgba(0,0,0,0.85))",
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ==========================================
// SCENE 2: MITOS VS FAKTA KOMISI (180 - 480 frames)
// ==========================================
const MythScene: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const alokFloat = Math.sin(frame / 20) * 8;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        transform: `scale(${pop})`,
      }}
    >
      {/* Eyebrow Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 24px",
          borderRadius: 30,
          backgroundColor: "rgba(239, 68, 68, 0.2)",
          border: "1.5px solid rgba(239, 68, 68, 0.5)",
          marginBottom: 20,
        }}
      >
        <XCircle size={22} color="#EF4444" />
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 26,
            letterSpacing: 2,
            color: "#F87171",
          }}
        >
          MITOS KELIRU GAMER
        </span>
      </div>

      <h1
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 90,
          lineHeight: 0.94,
          letterSpacing: 2.5,
          margin: "0 0 28px 0",
          textTransform: "uppercase",
          color: "#FFFFFF",
        }}
      >
        "DIAMOND MURAH PASTI
        <br />
        <span style={{ color: "#F87171" }}>ILEGAL & RAWAN MINUS?"</span>
      </h1>

      {/* Fact Card with Character Visual on Right */}
      <div
        style={{
          width: 920,
          height: 600,
          borderRadius: 28,
          backgroundColor: "rgba(18, 22, 32, 0.96)",
          border: "2px solid #38BDF8",
          boxShadow: "0 24px 60px rgba(14, 165, 233, 0.25)",
          padding: "32px 36px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Left: Text & Fee Box */}
        <div
          style={{
            width: 530,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 16,
            }}
          >
            <CheckCircle2 size={36} color="#34D399" />
            <span
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 42,
                letterSpacing: 2,
                color: "#34D399",
              }}
            >
              FAKTA: 100% LEGAL & RESMI!
            </span>
          </div>

          <p
            style={{
              fontSize: 24,
              fontWeight: 600,
              lineHeight: 1.45,
              color: "#CBD5E1",
              margin: "0 0 24px 0",
            }}
          >
            Bukan diamond bug, bukan kartu curian. Rahasia selisih harga murni ada
            di satu hal mendasar:
          </p>

          {/* 30% Fee Highlight Box */}
          <div
            style={{
              padding: "24px 26px",
              borderRadius: 18,
              backgroundColor: "rgba(239, 68, 68, 0.14)",
              border: "2px solid rgba(239, 68, 68, 0.45)",
              display: "flex",
              alignItems: "center",
              gap: 20,
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 16,
                backgroundColor: "rgba(239, 68, 68, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Percent size={42} color="#EF4444" />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 44,
                  lineHeight: 1,
                  color: "#F87171",
                  letterSpacing: 2,
                }}
              >
                KOMISI PLATFORM GOOGLE PLAY 30%
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "#E2E8F0",
                  marginTop: 6,
                }}
              >
                Potongan pihak ketiga yang bikin harga dalam game jadi melonjak
                mahal!
              </div>
            </div>
          </div>
        </div>

        {/* Right: Alok Character */}
        <div
          style={{
            width: 320,
            height: "100%",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            zIndex: 1,
          }}
        >
          <Img
            src={staticFile("ff/freefire_alok_official.png")}
            style={{
              height: 540,
              objectFit: "contain",
              transform: `translateY(${alokFloat}px)`,
              filter: "drop-shadow(0 16px 30px rgba(0,0,0,0.85))",
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ==========================================
// SCENE 3: BUKA-BUKAAN ANGKA RIIL (480 - 960 frames)
// ==========================================
const ComparisonScene: React.FC<{ frame: number; fps: number }> = ({
  frame,
  fps,
}) => {
  const pop1 = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const pop2 = spring({
    frame: frame - 15,
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* Eyebrow Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 24px",
          borderRadius: 30,
          backgroundColor: "rgba(56, 189, 248, 0.16)",
          border: "1.5px solid rgba(56, 189, 248, 0.4)",
          marginBottom: 16,
          transform: `scale(${pop1})`,
        }}
      >
        <Sparkles size={22} color="#38BDF8" />
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 26,
            letterSpacing: 2,
            color: "#38BDF8",
          }}
        >
          BUKA-BUKAAN ANGKA RIIL
        </span>
      </div>

      <h1
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 88,
          lineHeight: 0.94,
          letterSpacing: 2.5,
          margin: "0 0 24px 0",
          textTransform: "uppercase",
          color: "#FFFFFF",
          transform: `scale(${pop1})`,
        }}
      >
        PERBANDINGAN PAKET 720 DIAMOND
      </h1>

      {/* Card 1: GOOGLE PLAY IN-APP */}
      <div
        style={{
          width: 920,
          borderRadius: 22,
          backgroundColor: "rgba(18, 22, 32, 0.96)",
          border: "2px solid rgba(239, 68, 68, 0.45)",
          padding: "24px 32px",
          boxSizing: "border-box",
          textAlign: "left",
          marginBottom: 18,
          transform: `scale(${Math.max(0, pop1)})`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 24,
              letterSpacing: 1.5,
              color: "#F87171",
              backgroundColor: "rgba(239, 68, 68, 0.18)",
              padding: "4px 14px",
              borderRadius: 12,
            }}
          >
            GOOGLE PLAY (IN-APP)
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#94A3B8",
            }}
          >
            720 Diamond Free Fire
          </span>
        </div>

        <div
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 76,
            lineHeight: 1.05,
            color: "#FFFFFF",
            letterSpacing: 2,
            margin: "8px 0",
          }}
        >
          RP 140.000
        </div>

        <div
          style={{
            borderTop: "1.5px solid rgba(255,255,255,0.08)",
            paddingTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 20,
            fontWeight: 600,
            color: "#EF4444",
          }}
        >
          <div>• Terkena potongan komisi platform toko aplikasi ~30%</div>
          <div style={{ color: "#64748B" }}>
            • Biaya gateway tambahan + PPN in-app 11%
          </div>
        </div>
      </div>

      {/* Card 2: KYUSTORE OFFICIAL DISTRIBUTOR */}
      <div
        style={{
          width: 920,
          borderRadius: 24,
          backgroundColor: "rgba(14, 25, 40, 0.98)",
          border: "3px solid #38BDF8",
          boxShadow: "0 20px 45px rgba(14, 165, 233, 0.35)",
          padding: "26px 32px",
          boxSizing: "border-box",
          textAlign: "left",
          transform: `scale(${Math.max(0, pop2)})`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 24,
              letterSpacing: 1.5,
              color: "#34D399",
              backgroundColor: "rgba(52, 211, 153, 0.18)",
              padding: "4px 14px",
              borderRadius: 12,
            }}
          >
            KYUSTORE DIRECT GATEWAY
          </span>
          <span
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 24,
              letterSpacing: 1.5,
              color: "#0B0E14",
              backgroundColor: "#FBBF24",
              padding: "4px 14px",
              borderRadius: 12,
              fontWeight: 900,
            }}
          >
            HEMAT RP 46.500
          </span>
        </div>

        <div
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 82,
            lineHeight: 1.05,
            color: "#38BDF8",
            letterSpacing: 2,
            margin: "8px 0",
          }}
        >
          RP 93.500
        </div>

        <div
          style={{
            borderTop: "1.5px solid rgba(255,255,255,0.1)",
            paddingTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#FFFFFF",
            }}
          >
            <CheckCircle2 size={24} color="#34D399" />
            <span>Distributor Resmi Berizin (100% Legal & Bergaransi)</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#38BDF8",
            }}
          >
            <Zap size={24} color="#38BDF8" />
            <span>Zero Platform Tax (Tanpa Potongan Pihak Ketiga 30%)</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#94A3B8",
              fontSize: 20,
            }}
          >
            <CreditCard size={22} color="#94A3B8" />
            <span>Direct QRIS / E-Wallet / Virtual Account</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// SCENE 4: CLOSING & CALL TO ACTION (960 - 1380 frames)
// ==========================================
const CTAScene: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const pop = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const pulseScale = 1 + Math.sin(frame / 15) * 0.02;

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        transform: `scale(${pop})`,
      }}
    >
      {/* Eyebrow Pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 24px",
          borderRadius: 30,
          backgroundColor: "rgba(52, 211, 153, 0.16)",
          border: "1.5px solid rgba(52, 211, 153, 0.4)",
          color: "#34D399",
          marginBottom: 20,
        }}
      >
        <ShieldCheck size={24} color="#34D399" />
        <span
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 26,
            letterSpacing: 2,
            color: "#34D399",
          }}
        >
          BELANJA CERDAS & AMAN
        </span>
      </div>

      <h1
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 94,
          lineHeight: 0.94,
          letterSpacing: 3,
          margin: "0 0 32px 0",
          textTransform: "uppercase",
          color: "#FFFFFF",
        }}
      >
        BARANG SAMA. 100% LEGAL.
        <br />
        <span
          style={{
            background: "linear-gradient(135deg, #38BDF8 0%, #818CF8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          HEMATNYA NYATA BANGET!
        </span>
      </h1>

      {/* Trust Bento Grid (2 Cards) */}
      <div
        style={{
          width: 920,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 18,
          marginBottom: 32,
        }}
      >
        <div
          style={{
            padding: "24px 20px",
            borderRadius: 20,
            backgroundColor: "rgba(18, 22, 32, 0.95)",
            border: "1.5px solid rgba(255,255,255,0.12)",
            textAlign: "center",
          }}
        >
          <Zap size={36} color="#FBBF24" style={{ margin: "0 auto" }} />
          <div
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 38,
              lineHeight: 1,
              letterSpacing: 1.5,
              color: "#FFFFFF",
              marginTop: 10,
            }}
          >
            12 DETIK
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#94A3B8",
              marginTop: 4,
            }}
          >
            Proses Otomatis Instan
          </div>
        </div>

        <div
          style={{
            padding: "24px 20px",
            borderRadius: 20,
            backgroundColor: "rgba(18, 22, 32, 0.95)",
            border: "1.5px solid rgba(255,255,255,0.12)",
            textAlign: "center",
          }}
        >
          <Lock size={36} color="#34D399" style={{ margin: "0 auto" }} />
          <div
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 38,
              lineHeight: 1,
              letterSpacing: 1.5,
              color: "#FFFFFF",
              marginTop: 10,
            }}
          >
            ANTI-MINUS
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#94A3B8",
              marginTop: 4,
            }}
          >
            Garansi Uang Kembali
          </div>
        </div>
      </div>

      {/* Massive Punchy CTA Button */}
      <div
        style={{
          width: 920,
          padding: "26px 36px",
          borderRadius: 18,
          backgroundColor: "#0EA5E9",
          boxShadow: "0 20px 50px rgba(14, 165, 233, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          transform: `scale(${pulseScale})`,
        }}
      >
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: 1,
              color: "#082F49",
              textTransform: "uppercase",
            }}
          >
            CEK HARGA SEKARANG JUGA
          </div>
          <div
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 48,
              lineHeight: 1,
              letterSpacing: 2,
              color: "#0B0E14",
            }}
          >
            KYUSTORE.COM
          </div>
        </div>

        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: "50%",
            backgroundColor: "#0B0E14",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ArrowRight size={32} color="#38BDF8" />
        </div>
      </div>
    </div>
  );
};
